import React, { useState } from "react";
import { useWebSocketContext } from "@moonscape/shared";
import { ImportProgressPanel } from "@/components/ImportProgress/ImportProgressPanel";
import { AlertBar } from "@/components/ms";

interface SyncProgressBannerProps {
  exchangeId?: number;
  apiKeyId?: number;
  userId?: string;
}

interface SyncMessage {
  type: string;
  status: string;
  step?: {
    name: string;
    index: number;
    max_index: number;
    sub_progress?: {
      current: number;
      total: number;
      percentage: number;
      description: string;
    };
  };
  apiKey?: {
    id: number;
    exchange: {
      id: string;
      name: string;
    };
  };
}

export const SyncProgressBanner: React.FC<SyncProgressBannerProps> = ({
  exchangeId,
  apiKeyId,
  userId,
}) => {
  const { activeSyncs } = useWebSocketContext();
  const [dismissed, setDismissed] = useState(false);

  // Filter for processing syncs only
  const processingSyncs = Array.from(activeSyncs.values()).filter(
    (sync: SyncMessage) => sync.status === "processing"
  );

  if (processingSyncs.length === 0) {
    if (dismissed) setDismissed(false);
    return null;
  }
  if (dismissed) return null;

  const currentSync = processingSyncs[0];
  const currentExchangeId = currentSync.apiKey?.exchange?.id;
  const currentExchangeName = currentSync.apiKey?.exchange?.name;

  // Use ImportProgressPanel banner if we have exchange info
  if (currentExchangeId && currentExchangeName) {
    return (
      <ImportProgressPanel
        exchangeId={currentExchangeId}
        exchangeName={currentExchangeName}
        variant="banner"
      />
    );
  }

  // Fallback: original banner for messages without exchange info
  const subProgress = currentSync.step?.sub_progress;
  let progress = 0;
  if (currentSync.step) {
    const stepIndex = currentSync.step.index;
    const maxIndex = currentSync.step.max_index;
    if (subProgress?.percentage != null) {
      progress = (stepIndex * 100.0 / maxIndex) + (subProgress.percentage / maxIndex);
    } else {
      progress = (stepIndex / maxIndex) * 100;
    }
  }

  return (
    <AlertBar tone="live" bleed={false} onDismiss={() => setDismissed(true)}>
      {processingSyncs.length > 1 ? (
        <b>
          {processingSyncs.length} exchanges syncing — latest: {currentExchangeName}
        </b>
      ) : (
        <b>Syncing {currentExchangeName}</b>
      )}
      {subProgress ? (
        <>
          {" — "}
          {subProgress.current} of {subProgress.total}{" "}
          {subProgress.description || currentSync.step?.name?.toLowerCase() || "items"} (
          {Math.round(progress)}%)
        </>
      ) : (
        processingSyncs.length === 1 && (
          <>
            {" — "}Some exchanges return data in minutes, others can take hours depending on your
            history.
          </>
        )
      )}
    </AlertBar>
  );
};
